import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { listCabAmenities, listCabCategories, listCabLocations, searchCabs } from '../../api/cabsPublic';
import { getCategoryFallbackImage, omitEmptyParams } from './helpers';
import CabsBreadcrumbs from './CabsBreadcrumbs';
import CabsSearchWidget from './CabsSearchWidget';

const parseListParam = (value) => (value ? String(value).split(',').filter(Boolean) : []);

const CabsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState({ vehicles: [], routeInfo: null, pagination: { page: 1, totalPages: 1, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = useMemo(() => ({
    tripType: searchParams.get('tripType') === 'roundtrip' ? 'ROUNDTRIP' : 'ONEWAY',
    pickupLocationId: searchParams.get('pickupLocationId') || '',
    dropLocationId: searchParams.get('dropLocationId') || '',
    pickupDate: searchParams.get('pickupDate') || '',
    pickupTime: searchParams.get('pickupTime') || '',
    returnDate: searchParams.get('returnDate') || '',
    passengers: searchParams.get('passengers') || 1,
    categoryId: searchParams.get('categoryId') || '',
    seatConfigId: searchParams.get('seatConfigId') || '',
    amenityIds: parseListParam(searchParams.get('amenityIds')),
    minSeats: searchParams.get('minSeats') || '',
    maxSeats: searchParams.get('maxSeats') || '',
    priceMin: searchParams.get('priceMin') || '',
    priceMax: searchParams.get('priceMax') || '',
    sort: searchParams.get('sort') || 'price_low',
    page: Number(searchParams.get('page') || 1),
  }), [searchParams]);

  useEffect(() => {
    listCabCategories().then((res) => setCategories(res.data || res || [])).catch(() => {});
    listCabAmenities().then((res) => setAmenities(res.data || res || [])).catch(() => {});
    listCabLocations().then((res) => setLocations(res.data || res || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await searchCabs({
          tripType: filters.tripType === 'ROUNDTRIP' ? 'roundtrip' : 'oneway',
          pickupLocationId: filters.pickupLocationId,
          dropLocationId: filters.dropLocationId,
          pickupDate: filters.pickupDate,
          pickupTime: filters.pickupTime,
          returnDate: filters.tripType === 'ROUNDTRIP' ? filters.returnDate : undefined,
          passengers: filters.passengers,
          categoryId: filters.categoryId || undefined,
          seatConfigId: filters.seatConfigId || undefined,
          amenityIds: filters.amenityIds.length ? filters.amenityIds.join(',') : undefined,
          minSeats: filters.minSeats || undefined,
          maxSeats: filters.maxSeats || undefined,
          priceMin: filters.priceMin || undefined,
          priceMax: filters.priceMax || undefined,
          sort: filters.sort,
          page: filters.page,
          limit: 9,
        });
        if (!active) return;
        setData(res.data || res || { vehicles: [], routeInfo: null, pagination: { page: 1, totalPages: 1, total: 0 } });
      } catch {
        if (!active) return;
        setData({ vehicles: [], routeInfo: null, pagination: { page: 1, totalPages: 1, total: 0 } });
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [filters]);

  const locationMap = useMemo(() => {
    const map = {};
    locations.forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [locations]);

  const updateFilters = (patch = {}, resetPage = true) => {
    const next = {
      ...filters,
      ...patch,
      page: resetPage ? 1 : patch.page || filters.page,
    };
    const params = omitEmptyParams({
      tripType: next.tripType === 'ROUNDTRIP' ? 'roundtrip' : 'oneway',
      pickupLocationId: next.pickupLocationId,
      dropLocationId: next.dropLocationId,
      pickupDate: next.pickupDate,
      pickupTime: next.pickupTime,
      returnDate: next.tripType === 'ROUNDTRIP' ? next.returnDate : '',
      passengers: next.passengers,
      categoryId: next.categoryId,
      seatConfigId: next.seatConfigId,
      amenityIds: next.amenityIds.length ? next.amenityIds.join(',') : '',
      minSeats: next.minSeats,
      maxSeats: next.maxSeats,
      priceMin: next.priceMin,
      priceMax: next.priceMax,
      sort: next.sort,
      page: next.page > 1 ? next.page : '',
    });
    navigate(`/cabs/list${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const clearFilters = () => {
    updateFilters({
      categoryId: '',
      seatConfigId: '',
      amenityIds: [],
      minSeats: '',
      maxSeats: '',
      priceMin: '',
      priceMax: '',
      sort: 'price_low',
    });
  };

  const handleSearch = (values) => {
    updateFilters(
      {
        tripType: values.tripType,
        pickupLocationId: values.pickupLocationId,
        dropLocationId: values.dropLocationId,
        pickupDate: values.pickupDate,
        pickupTime: values.pickupTime,
        returnDate: values.returnDate,
        passengers: values.passengers,
      },
      true
    );
  };

  const filterPanel = (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Vehicle Category</label>
        <select
          className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
          value={filters.categoryId}
          onChange={(e) => updateFilters({ categoryId: e.target.value })}
        >
          <option value="">All categories</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Min Seats</label>
          <input
            type="number"
            min="1"
            className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
            value={filters.minSeats}
            onChange={(e) => updateFilters({ minSeats: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Max Seats</label>
          <input
            type="number"
            min="1"
            className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
            value={filters.maxSeats}
            onChange={(e) => updateFilters({ maxSeats: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Min Budget</label>
          <input
            type="number"
            min="0"
            className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
            value={filters.priceMin}
            onChange={(e) => updateFilters({ priceMin: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Max Budget</label>
          <input
            type="number"
            min="0"
            className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
            value={filters.priceMax}
            onChange={(e) => updateFilters({ priceMax: e.target.value })}
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Amenities</label>
        <div className="mt-3 space-y-2">
          {amenities.map((amenity) => {
            const checked = filters.amenityIds.includes(amenity.id);
            return (
              <label key={amenity.id} className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    updateFilters({
                      amenityIds: checked
                        ? filters.amenityIds.filter((id) => id !== amenity.id)
                        : [...filters.amenityIds, amenity.id],
                    })
                  }
                />
                {amenity.name}
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Sort By</label>
        <select
          className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm"
          value={filters.sort}
          onChange={(e) => updateFilters({ sort: e.target.value })}
        >
          <option value="price_low">Price low to high</option>
          <option value="price_high">Price high to low</option>
          <option value="seats_high">Highest seats</option>
        </select>
      </div>

      <button className="w-full rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700" onClick={clearFilters}>
        Clear all
      </button>
    </div>
  );

  const summaryPickup = data.routeInfo?.fromLocation?.name || locationMap[filters.pickupLocationId]?.name || 'Pickup';
  const summaryDrop = data.routeInfo?.toLocation?.name || locationMap[filters.dropLocationId]?.name || 'Drop';

  return (
    <section className="space-y-6">
      <CabsBreadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Cabs', to: '/cabs' }, { label: 'Listing' }]} />

      <div className="rounded-[28px] border border-slate-200 bg-white/95 p-4 shadow-sm sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Search Results</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-900">
              {summaryPickup} to {summaryDrop}
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {filters.tripType === 'ROUNDTRIP' ? 'Round trip' : 'One way'} | {filters.pickupDate || 'Pickup date'} | {filters.passengers} passengers
            </p>
          </div>
          <button
            type="button"
            className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 lg:hidden"
            onClick={() => setFiltersOpen(true)}
          >
            Filters
          </button>
        </div>
        <CabsSearchWidget compact initialValues={filters} onSubmit={handleSearch} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[290px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
            <div className="mt-4">{filterPanel}</div>
          </div>
        </aside>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Available vehicles</h2>
              <p className="text-sm text-slate-500">{data.pagination.total || 0} matching results</p>
            </div>
          </div>

          {loading && (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="h-[380px] animate-pulse rounded-[24px] border border-slate-200 bg-white" />
              ))}
            </div>
          )}

          {!loading && !data.vehicles.length && (
            <div className="rounded-[24px] border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              {!filters.pickupLocationId || !filters.dropLocationId
                ? 'Choose pickup and drop locations to see live ride results.'
                : 'No cab options match the current filters. Adjust seats, budget, or amenities and try again.'}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {data.vehicles.map((vehicle) => {
              const cover = vehicle.images?.find((item) => item.isCover) || vehicle.images?.[0];
              const imageUrl = cover?.url || vehicle.category?.imageUrl || getCategoryFallbackImage(vehicle.category?.name);
              const estimate = vehicle.estimatedPricing || null;
              const enquiryParams = omitEmptyParams({
                tripType: filters.tripType === 'ROUNDTRIP' ? 'roundtrip' : 'oneway',
                pickupLocationId: filters.pickupLocationId,
                dropLocationId: filters.dropLocationId,
                pickupDate: filters.pickupDate,
                pickupTime: filters.pickupTime,
                returnDate: filters.returnDate,
                passengers: filters.passengers,
              }).toString();

              return (
                <article key={vehicle.id} className="flex h-full flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
                  <div className="h-52 overflow-hidden bg-slate-100">
                    {imageUrl ? (
                      <img src={imageUrl} alt={vehicle.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-slate-400">No image</div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                          {vehicle.category?.name || 'Vehicle'}
                        </p>
                        <h3 className="line-clamp-2 text-lg font-semibold text-slate-900">{vehicle.name}</h3>
                      </div>
                      <span className="rounded-full bg-[#edf7f5] px-2.5 py-1 text-[11px] font-semibold text-[#0d6b66]">
                        {vehicle.seatConfig?.seatCapacity || '-'} seats
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {(vehicle.amenities || []).slice(0, 3).map((amenity) => (
                        <span key={amenity.id} className="rounded-full bg-slate-100 px-3 py-1 text-[11px] text-slate-600">
                          {amenity.amenity?.name || amenity.name}
                        </span>
                      ))}
                    </div>

                    <div className="mt-4 flex-1 text-sm text-slate-600">
                      <div className="flex items-center justify-between">
                        <span>Route</span>
                        <span>{summaryPickup} to {summaryDrop}</span>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <span>Trip type</span>
                        <span>{filters.tripType === 'ROUNDTRIP' ? 'Round trip' : 'One way'}</span>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <span>Estimate</span>
                        <span className="font-semibold text-slate-900">
                          {estimate?.total ? `INR ${Number(estimate.total).toLocaleString()}` : 'Unavailable'}
                        </span>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-2">
                      <Link
                        to={`/cabs/${vehicle.id}?${enquiryParams}`}
                        className="inline-flex rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white no-underline"
                      >
                        View details
                      </Link>
                      <Link
                        to={`/cabs/${vehicle.id}/enquiry?${enquiryParams}`}
                        className="inline-flex rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 no-underline"
                      >
                        Enquire now
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {!loading && data.pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-[24px] border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
              <span>
                Page {data.pagination.page} of {data.pagination.totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold"
                  disabled={data.pagination.page <= 1}
                  onClick={() => updateFilters({ page: Math.max(1, data.pagination.page - 1) }, false)}
                >
                  Prev
                </button>
                <button
                  className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold"
                  disabled={data.pagination.page >= data.pagination.totalPages}
                  onClick={() => updateFilters({ page: data.pagination.page + 1 }, false)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/50 p-4 lg:hidden">
          <div className="h-full overflow-y-auto rounded-[28px] bg-white p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
              <button
                type="button"
                className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700"
                onClick={() => setFiltersOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="mt-4">{filterPanel}</div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CabsList;
